import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import DBButton from "../../component/global/DBButton";
import DBModal from "../../component/global/DBModal";
import DBSpinner from "../../component/global/DBSpinner";
import { ToastContext } from "../../component/global/ToastContext";
import { useRequest } from "../../hooks/helperData/useRequest";
import { surveyDetailUrl } from "../../helperData/ApiAddress";
import FeatureFieldsEditor from "./FeatureFieldsEditor";
import {
    featuresFromApi,
    featuresToPayload,
    validateFeatureRows,
} from "./featureFields";
import { translateServerDetail } from "./translateServerDetail";

export default function SurveyFeaturesModal({
    show,
    survey,
    stacked,
    onHide,
    onSuccess,
}) {
    const toast = useContext(ToastContext);
    const [features, setFeatures] = useState([]);
    const [errors, setErrors] = useState([]);
    const [version, setVersion] = useState(1);
    const [anonymous, setAnonymous] = useState(false);

    const [getSurvey, getSurveyLoading] = useRequest(
        survey?.id ? surveyDetailUrl(survey.id) : "",
        "GET"
    );
    const [saveSurvey, saveLoading] = useRequest(
        survey?.id ? surveyDetailUrl(survey.id) : "",
        "PATCH"
    );

    useEffect(() => {
        if (!show || !survey?.id) return;
        setErrors([]);
        getSurvey()
            .then((row) => {
                setFeatures(featuresFromApi(row.features));
                setVersion(row.version);
                setAnonymous(Boolean(row.anonymous_responses));
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(detail || t("survey.load.error"), "danger");
                onHide?.();
            });
    }, [show, survey?.id]);

    const handleSave = () => {
        const nextErrors = validateFeatureRows(features);
        setErrors(nextErrors);
        if (nextErrors.some(Boolean)) return;
        saveSurvey({
            features: featuresToPayload(features),
            version,
        })
            .then((saved) => {
                toast(t("survey.features.save.success"), "success");
                onSuccess?.(saved);
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail ||
                        e.response?.data?.features,
                    t
                );
                toast(detail || t("survey.features.save.error"), "danger");
            });
    };

    return (
        <DBModal
            permanent
            stacked={stacked}
            show={show}
            minWidth={720}
            onHide={onHide}
            className="survey-form-modal"
        >
            <DBModal.Header
                closeBtn={onHide}
                closeLabel={t("survey.settings.help.close")}
                title={t("survey.features.edit.title")}
            />
            <DBModal.Content>
                {getSurveyLoading ? (
                    <DBSpinner tip={t("loading")} />
                ) : (
                    <>
                        <p className="survey-wizard-lead">
                            {t("survey.features.lead")}
                        </p>
                        <p className="survey-wizard-help">
                            {anonymous
                                ? t("survey.features.anonymous.help")
                                : t("survey.features.help")}
                        </p>
                        <FeatureFieldsEditor
                            features={features}
                            onChange={(next) => {
                                setFeatures(next);
                                setErrors([]);
                            }}
                            errors={errors}
                        />
                    </>
                )}
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    caption={t("save")}
                    onClick={handleSave}
                    loading={saveLoading}
                />
                <DBButton
                    caption={t("cancel")}
                    type="secondary"
                    onClick={onHide}
                />
            </DBModal.Footer>
        </DBModal>
    );
}
